import { useEffect, useState } from "react";
import { SpiceLevel } from "@badorbad/shared";

const KEY = "badorbad.spice";
const ADULT_KEY = "badorbad.adult";

/**
 * The player's spice preference, remembered across sessions, and the adult
 * confirmation that guards the top level.
 *
 * plan.md §1.4: the selection persists until the player changes it, and every
 * random request uses the current value. Kept in localStorage rather than state
 * so it survives a refresh — a mid-game reload should not silently reset how
 * bad the suggestions are.
 *
 * The gate lives in here rather than in the screen on purpose. A second place
 * that offers the selector would otherwise be able to set spice 3 without ever
 * asking, and the rule would quietly hold in one screen only.
 *
 * Both values stay on the device and are never sent to the server. Storing the
 * confirmation server-side would mean attaching an age claim to an identity we
 * otherwise keep empty on purpose — more data, longer retention, no benefit to
 * the player. See the privacy page, which says exactly this.
 *
 * Device-local for now. Once accounts exist the spice level belongs on the
 * profile alongside the avatar seed, so it follows the player between devices.
 * The confirmation should not follow: it is a statement about the person using
 * this device.
 */
const read = (): SpiceLevel => {
  try {
    const raw = Number(localStorage.getItem(KEY));
    // Anything unexpected — absent, corrupted, out of range — falls back to
    // the documented default rather than trusting stored input.
    if (raw === 1 || raw === 2 || raw === 3) return raw;
  } catch {
    // Private browsing and disabled storage both throw; the default is fine.
  }
  return SpiceLevel.Medium;
};

const readConfirmed = (): boolean => {
  try {
    return localStorage.getItem(ADULT_KEY) === "true";
  } catch {
    return false;
  }
};

export const useSpicePreference = () => {
  const [spice, setSpice] = useState<SpiceLevel>(read);
  const [confirmed, setConfirmed] = useState<boolean>(readConfirmed);
  /** True while the player is being asked to confirm their age. */
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, String(spice));
    } catch {
      // Not being able to remember is not a reason to break the game.
    }
  }, [spice]);

  useEffect(() => {
    if (!confirmed) return;
    try {
      localStorage.setItem(ADULT_KEY, "true");
    } catch {
      // Storage refused: the player is asked again next time, which is the
      // safe direction to fail in.
    }
  }, [confirmed]);

  /**
   * Never sets spice 3 directly. An unconfirmed player raises the prompt
   * instead, and the level only changes if they say yes — so cancelling leaves
   * them exactly where they were rather than on the level they backed out of.
   */
  const requestSpice = (next: SpiceLevel) => {
    if (next === SpiceLevel.Hot && !confirmed) {
      setAsking(true);
      return;
    }
    setSpice(next);
  };

  const confirmAdult = () => {
    setConfirmed(true);
    setAsking(false);
    setSpice(SpiceLevel.Hot);
  };

  const cancelAdult = () => setAsking(false);

  return { spice, requestSpice, asking, confirmAdult, cancelAdult } as const;
};
